import { useEffect, useRef, useState } from "react";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";
import { toast } from "sonner-native";

import { ScreenLayout } from "@/blocks/ScreenLayout";
import { SecureTextEntryToggle } from "@/blocks/SecureTextEntryToggle";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import {
  useConfirmPasswordResetMutation,
  useSignInWithEmailAndPasswordMutation,
  useVerifyPasswordResetCodeMutation,
} from "@/data/mutations/authMutations";
import { resetPasswordFormSchema, ResetPasswordFormType } from "@/domain/auth";
import { useTranslator } from "@/hooks/useTranslate";
import { mfaResolverAtom } from "@/stores/atoms";
import { logger } from "@/utils/logger";
import { handleMfaError } from "@/utils/mfa";
import { normalizeSearchParam } from "@/utils/routing";

const ResetPasswordScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const { oobCode } = useLocalSearchParams<{ oobCode: string }>();
  const normalizedOobCode = normalizeSearchParam(oobCode);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const setMfaResolver = useSetAtom(mfaResolverAtom);

  const { mutate: verifyPasswordResetCode } =
    useVerifyPasswordResetCodeMutation();
  const { mutateAsync: confirmPasswordReset } =
    useConfirmPasswordResetMutation();
  const { mutate: signIn } = useSignInWithEmailAndPasswordMutation();

  const hasAttemptedVerify = useRef(false);

  const resetPasswordForm = useForm<ResetPasswordFormType>({
    defaultValues: { password: "", confirmPassword: "" },
    resolver: standardSchemaResolver(resetPasswordFormSchema),
  });

  const [password, confirmPassword] = resetPasswordForm.watch([
    "password",
    "confirmPassword",
  ]);
  const isUpdateDisabled = !password || !confirmPassword;

  useEffect(() => {
    if (hasAttemptedVerify.current) return;

    const redirectWithError = () => {
      toast.error(t("native:resetPasswordScreen.invalidResetLink"));
      setShouldRedirect(true);
    };

    if (!normalizedOobCode) {
      redirectWithError();
      return;
    }

    hasAttemptedVerify.current = true;

    // Validate the reset code up front (catches expired/used links) and derive
    // the email so we can auto sign-in after the password is reset.
    verifyPasswordResetCode(normalizedOobCode, {
      onSuccess: verifiedEmail => {
        setEmail(verifiedEmail);
        setIsVerifyingCode(false);
      },
      onError: error => {
        logger.error({
          message: "Failed to verify password reset code",
          error,
        });
        redirectWithError();
      },
    });
  }, [normalizedOobCode, t, verifyPasswordResetCode]);

  const handleSubmit = async (data: ResetPasswordFormType) => {
    if (!normalizedOobCode || !email) return;

    setIsLoading(true);

    try {
      await confirmPasswordReset({
        oobCode: normalizedOobCode,
        password: data.password,
      });
    } catch (error) {
      setIsLoading(false);
      logger.error({ message: "Failed to confirm password reset", error });
      resetPasswordForm.setError("password", {
        message: t("common:validation.genericError"),
      });
      return;
    }

    // Auto sign-in so the existing MFA challenge fires when the user is enrolled.
    signIn(
      { email, password: data.password },
      {
        onError: error => {
          if (
            handleMfaError(error, resolver => {
              setMfaResolver(resolver);
              router.push("/(unauthed)/mfa-verify");
            })
          ) {
            setIsLoading(false);
            return;
          }
          setIsLoading(false);
          logger.error({
            message: "Failed to sign in after password reset",
            error,
          });
          resetPasswordForm.setError("password", {
            message: t("common:validation.genericError"),
          });
        },
      },
    );
  };

  if (shouldRedirect) {
    return <Redirect href="/(unauthed)/auth" />;
  }

  if (isVerifyingCode) {
    return (
      <ScreenLayout className="py-12">
        <VnBox className="flex-1 items-center justify-center">
          <VnLoadingSpinner />
        </VnBox>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout className="py-12">
      <VnBox className="mt-safe-offset-10 web:mt-10 flex items-center px-4 sm:my-auto md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          <VnBox className="flex flex-col gap-6">
            <VnBox className="gap-2">
              <VnText
                variant="headerOne"
                i18nKey="native:resetPasswordScreen.title"
                className="text-center"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground text-center"
                i18nKey="native:resetPasswordScreen.subtitle"
              />
            </VnBox>
            <VnForm form={resetPasswordForm} isInputOnlyForm>
              <VnCard className="gap-6 rounded-2xl px-4">
                <VnTextInput
                  control={resetPasswordForm.control}
                  name="password"
                  labelI18nKey="native:resetPasswordScreen.passwordInputLabel"
                  placeholder={t(
                    "native:resetPasswordScreen.passwordInputPlaceholder",
                  )}
                  autoCapitalize="none"
                  autoCorrect={false}
                  secureTextEntry={!isPasswordVisible}
                  isRequired
                  rightElement={
                    <SecureTextEntryToggle
                      isVisible={isPasswordVisible}
                      onPress={() => setIsPasswordVisible(prev => !prev)}
                    />
                  }
                />
                <VnTextInput
                  control={resetPasswordForm.control}
                  name="confirmPassword"
                  labelI18nKey="native:resetPasswordScreen.confirmPasswordInputLabel"
                  placeholder={t(
                    "native:resetPasswordScreen.confirmPasswordInputPlaceholder",
                  )}
                  autoCapitalize="none"
                  autoCorrect={false}
                  secureTextEntry={!isConfirmPasswordVisible}
                  returnKeyType="done"
                  enablesReturnKeyAutomatically
                  onSubmitEditing={() => {
                    void resetPasswordForm.handleSubmit(handleSubmit)();
                  }}
                  isRequired
                  rightElement={
                    <SecureTextEntryToggle
                      isVisible={isConfirmPasswordVisible}
                      onPress={() => setIsConfirmPasswordVisible(prev => !prev)}
                    />
                  }
                />
                <VnButton
                  onPress={() => {
                    void resetPasswordForm.handleSubmit(handleSubmit)();
                  }}
                  i18nKey="native:resetPasswordScreen.updatePasswordButton"
                  isLoading={isLoading}
                  className="rounded-xl"
                  disabled={isUpdateDisabled}
                />
              </VnCard>
            </VnForm>
          </VnBox>
        </VnBox>
      </VnBox>
    </ScreenLayout>
  );
};

export { ResetPasswordScreen };
